import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AdminAPI } from "@/lib/api";
import { getAuthToken, getAuthEmail, clearAuthSession } from "@/lib/authStorage";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, LogOut, ExternalLink } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const STATUSES = ["new", "contacted", "booked", "archived"];

export default function Admin() {
    const nav = useNavigate();
    const [tab, setTab] = useState("inquiries");
    const [inquiries, setInquiries] = useState([]);
    const [printInq, setPrintInq] = useState([]);
    const [mentoring, setMentoring] = useState([]);
    const [posts, setPosts] = useState([]);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [selectedPrint, setSelectedPrint] = useState(null);
    const [selectedMentor, setSelectedMentor] = useState(null);
    const [editor, setEditor] = useState(null); // {mode:'create'|'edit', data}

    const refresh = useCallback(async () => {
        try {
            const [a, b, c, d] = await Promise.all([
                AdminAPI.listInquiries(),
                AdminAPI.listPrintInquiries(),
                AdminAPI.listMentoring(),
                AdminAPI.listJournal(),
            ]);
            setInquiries(a); setPrintInq(b); setMentoring(c); setPosts(d);
        } catch (err) {
            toast.error("Failed to load data");
        }
    }, []);

    useEffect(() => {
        const token = getAuthToken();
        if (!token) {
            nav("/admin/login");
            return;
        }
        AdminAPI.me().catch(() => {
            clearAuthSession();
            nav("/admin/login");
        });
        refresh();
    }, [nav, refresh]);

    const logout = () => {
        clearAuthSession();
        nav("/admin/login");
    };

    const updateStatus = async (id, status) => {
        try {
            await AdminAPI.updateInquiry(id, status);
            toast.success("Status updated");
            refresh();
        } catch { toast.error("Failed to update"); }
    };

    const savePost = async (data) => {
        try {
            if (editor.mode === "create") await AdminAPI.createJournal(data);
            else await AdminAPI.updateJournal(editor.data.id, data);
            toast.success(editor.mode === "create" ? "Post created" : "Post updated");
            setEditor(null); refresh();
        } catch { toast.error("Failed to save"); }
    };

    const deletePost = async (id) => {
        if (!window.confirm("Delete this post?")) return;
        try { await AdminAPI.deleteJournal(id); toast.success("Deleted"); refresh(); }
        catch { toast.error("Failed to delete"); }
    };

    const adminEmail = getAuthEmail();

    const statusBadge = (s) => {
        const colorMap = {
            new: "bg-[hsl(var(--lux-oxblood))]/10 text-[hsl(var(--lux-oxblood))] border-[hsl(var(--lux-oxblood))]/30",
            contacted: "bg-[hsl(var(--lux-brass))]/15 text-[hsl(var(--lux-brass))] border-[hsl(var(--lux-brass))]/40",
            booked: "bg-[hsl(var(--lux-sage))]/15 text-[hsl(var(--lux-sage))] border-[hsl(var(--lux-sage))]/40",
            archived: "bg-muted text-muted-foreground border-border",
        };
        return <Badge variant="outline" className={`uppercase tracking-[0.16em] text-[10px] ${colorMap[s] || ""}`}>{s}</Badge>;
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--lux-paper))]">
            <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/90 backdrop-blur border-b border-border">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div>
                        <div className="font-serif-display text-lg leading-none">Studio · Admin</div>
                        <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-1">{adminEmail}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="/" target="_blank" rel="noreferrer" className="text-xs tracking-[0.18em] uppercase lux-underline inline-flex items-center gap-2">
                            View Site <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <Button onClick={logout} data-testid="admin-logout-button" variant="ghost" className="text-xs tracking-[0.18em] uppercase">
                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-transparent p-0 gap-2 mb-6 flex-wrap h-auto">
                        <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-4 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Booking Inquiries ({inquiries.length})
                        </TabsTrigger>
                        <TabsTrigger value="prints" data-testid="admin-tab-prints" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-4 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Print Requests ({printInq.length})
                        </TabsTrigger>
                        <TabsTrigger value="mentoring" data-testid="admin-tab-mentoring" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-4 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Mentoring ({mentoring.length})
                        </TabsTrigger>
                        <TabsTrigger value="journal" data-testid="admin-tab-journal" className="data-[state=active]:bg-foreground data-[state=active]:text-background rounded-full px-4 h-9 text-[11px] tracking-[0.18em] uppercase">
                            Journal ({posts.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* BOOKING INQUIRIES */}
                    <TabsContent value="inquiries">
                        <Card data-testid="admin-inquiries-table">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Session</TableHead>
                                        <TableHead>Budget</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {inquiries.length === 0 && (
                                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No inquiries yet.</TableCell></TableRow>
                                    )}
                                    {inquiries.map((i) => (
                                        <TableRow key={i.id} className="hover:bg-[hsl(var(--lux-porcelain))]">
                                            <TableCell className="text-xs whitespace-nowrap">{fmt(i.created_at)}</TableCell>
                                            <TableCell className="font-serif-display">{i.full_name}</TableCell>
                                            <TableCell className="text-xs">{i.email}</TableCell>
                                            <TableCell className="text-xs">{i.session_type || "—"}</TableCell>
                                            <TableCell className="text-xs">{i.budget_range || "—"}</TableCell>
                                            <TableCell>
                                                <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                                                    <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell><Button variant="ghost" size="sm" onClick={() => setSelectedInquiry(i)}>View</Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>

                    {/* PRINT REQUESTS */}
                    <TabsContent value="prints">
                        <Card>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Print</TableHead>
                                        <TableHead>Size / Paper</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {printInq.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No print requests yet.</TableCell></TableRow>}
                                    {printInq.map((i) => (
                                        <TableRow key={i.id} className="hover:bg-[hsl(var(--lux-porcelain))]">
                                            <TableCell className="text-xs whitespace-nowrap">{fmt(i.created_at)}</TableCell>
                                            <TableCell className="font-serif-display">{i.full_name}</TableCell>
                                            <TableCell className="text-xs">{i.email}</TableCell>
                                            <TableCell className="text-xs">{i.print_title || i.print_id}</TableCell>
                                            <TableCell className="text-xs">{i.size} · {i.paper_type}</TableCell>
                                            <TableCell><Button variant="ghost" size="sm" onClick={() => setSelectedPrint(i)}>View</Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>

                    {/* MENTORING */}
                    <TabsContent value="mentoring">
                        <Card>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Offer</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {mentoring.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No applications yet.</TableCell></TableRow>}
                                    {mentoring.map((i) => (
                                        <TableRow key={i.id} className="hover:bg-[hsl(var(--lux-porcelain))]">
                                            <TableCell className="text-xs whitespace-nowrap">{fmt(i.created_at)}</TableCell>
                                            <TableCell className="font-serif-display">{i.full_name}</TableCell>
                                            <TableCell className="text-xs">{i.email}</TableCell>
                                            <TableCell className="text-xs">{i.offer_type || "—"}</TableCell>
                                            <TableCell><Button variant="ghost" size="sm" onClick={() => setSelectedMentor(i)}>View</Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>

                    {/* JOURNAL CMS */}
                    <TabsContent value="journal">
                        <div className="mb-5 flex items-center justify-between">
                            <h2 className="font-serif-display text-2xl">Journal Posts</h2>
                            <Button
                                data-testid="admin-create-post"
                                onClick={() => setEditor({ mode: "create", data: { title: "", excerpt: "", body: "", cover_image: "", category: "", published: false } })}
                                className="bg-foreground text-background rounded-full h-10 px-5 text-[11px] tracking-[0.22em] uppercase"
                            >
                                <Plus className="h-4 w-4 mr-2" /> New Post
                            </Button>
                        </div>
                        <Card>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Title</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Updated</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {posts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No posts yet.</TableCell></TableRow>}
                                    {posts.map((p) => (
                                        <TableRow key={p.id} data-testid={`admin-post-row-${p.id}`} className="hover:bg-[hsl(var(--lux-porcelain))]">
                                            <TableCell className="font-serif-display">{p.title}</TableCell>
                                            <TableCell className="text-xs">{p.category || "—"}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={p.published ? "border-[hsl(var(--lux-sage))]/40 text-[hsl(var(--lux-sage))]" : "border-border text-muted-foreground"}>
                                                    {p.published ? "Published" : "Draft"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">{fmt(p.updated_at)}</TableCell>
                                            <TableCell className="flex gap-2">
                                                <Button variant="ghost" size="sm" data-testid={`admin-edit-post-${p.id}`} onClick={() => setEditor({ mode: "edit", data: p })}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" data-testid={`admin-delete-post-${p.id}`} onClick={() => deletePost(p.id)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>

            {/* Inquiry details dialogs */}
            <DetailDialog open={!!selectedInquiry} onClose={() => setSelectedInquiry(null)} title={selectedInquiry?.full_name} subtitle="Booking Inquiry" data={selectedInquiry} statusBadge={statusBadge} />
            <DetailDialog open={!!selectedPrint} onClose={() => setSelectedPrint(null)} title={selectedPrint?.full_name} subtitle="Print Request" data={selectedPrint} />
            <DetailDialog open={!!selectedMentor} onClose={() => setSelectedMentor(null)} title={selectedMentor?.full_name} subtitle="Mentoring Application" data={selectedMentor} />

            <PostEditor editor={editor} onClose={() => setEditor(null)} onSave={savePost} />
        </div>
    );
}

const Card = ({ children }) => (
    <div className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] overflow-x-auto">{children}</div>
);

const DetailDialog = ({ open, onClose, title, subtitle, data, statusBadge }) => (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-xl">
            <DialogHeader>
                <div className="kicker">{subtitle}</div>
                <DialogTitle className="font-serif-display text-2xl">{title}</DialogTitle>
            </DialogHeader>
            {data && (
                <div className="space-y-3 text-sm">
                    {statusBadge && <div>{statusBadge(data.status)}</div>}
                    <Separator className="bg-border" />
                    {Object.entries(data).map(([k, v]) => {
                        if (["id", "_id"].includes(k) || v === null || v === undefined || v === "") return null;
                        return (
                            <div key={k} className="grid grid-cols-3 gap-3">
                                <div className="kicker col-span-1">{k.replace(/_/g, " ")}</div>
                                <div className="col-span-2 break-words">{String(v)}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </DialogContent>
    </Dialog>
);

const PostEditor = ({ editor, onClose, onSave }) => {
    const [data, setData] = useState(editor?.data || null);
    useEffect(() => { setData(editor?.data || null); }, [editor]);
    if (!editor || !data) return null;
    const set = (k, v) => setData({ ...data, [k]: v });
    return (
        <Dialog open={!!editor} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-journal-editor">
                <DialogHeader>
                    <div className="kicker">Journal</div>
                    <DialogTitle className="font-serif-display text-2xl">{editor.mode === "create" ? "New Post" : "Edit Post"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <Field label="Title"><Input data-testid="admin-post-title" value={data.title} onChange={(e) => set("title", e.target.value)} /></Field>
                    <Field label="Category"><Input data-testid="admin-post-category" value={data.category || ""} onChange={(e) => set("category", e.target.value)} /></Field>
                    <Field label="Cover image URL"><Input data-testid="admin-post-cover" placeholder="https://" value={data.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} /></Field>
                    <Field label="Excerpt"><Textarea rows={2} value={data.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} /></Field>
                    <Field label="Body (HTML supported)"><Textarea data-testid="admin-post-body" rows={10} value={data.body || ""} onChange={(e) => set("body", e.target.value)} /></Field>
                    <div className="flex items-center gap-3">
                        <Switch data-testid="admin-post-published" checked={!!data.published} onCheckedChange={(v) => set("published", v)} />
                        <span className="text-sm">Published</span>
                    </div>
                </div>
                <DialogFooter className="mt-6">
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button
                        data-testid="admin-save-post"
                        onClick={() => onSave({
                            title: data.title, excerpt: data.excerpt, body: data.body,
                            cover_image: data.cover_image, category: data.category, published: !!data.published,
                        })}
                        className="bg-foreground text-background rounded-full h-10 px-5 text-[11px] tracking-[0.22em] uppercase"
                    >
                        {editor.mode === "create" ? "Create Post" : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="kicker block mb-2">{label}</span>
        {children}
    </label>
);
